import React__default, { useRef, useCallback } from 'react';
import { Tooltip } from '@shopify/polaris';
import classNames from 'classnames';
import { isIOSUserAgent } from '../../../../../../utilities/user-agent.mjs';
import styles from './Button.scss.mjs';
import { useMediaQuery } from '../../../../../MediaQueryProvider/MediaQueryProvider.mjs';
import { PreventBlur } from '../../../PreventBlur/PreventBlur.mjs';
import { PlainAction } from '../../../../../PlainAction/PlainAction.mjs';

// TODO: Do we want to specify a `preferredPosition`?

function Button({
  accessibilityLabel,
  active,
  ariaExpanded,
  content,
  disabled,
  disclosure,
  icon,
  pressed,
  tooltip,
  onAction
}) {
  const {
    mobile
  } = useMediaQuery();
  const nodeRef = useRef(null);
  const onClickTriggered = useRef(false);
  const onClickTimeoutId = useRef();
  const handleAction = useCallback(() => {
    onClickTriggered.current = true;
    onAction == null ? void 0 : onAction();
  }, [onAction]);
  const handlePointerDown = useCallback(() => {
    onClickTriggered.current = false;
    if (onClickTimeoutId.current) {
      clearTimeout(onClickTimeoutId.current);
      onClickTimeoutId.current = undefined;
    }
  }, []);
  const handlePointerUp = useCallback(event => {
    // On `iOS` devices, there is a safe radius around text selections
    // that prevents `click` events from being triggered. We listen for
    // pointer events instead to ensure that the `onAction` callback is
    // called whenever a toolbar button is pressed
    if (isIOSUserAgent() && onClickTriggered.current === false) {
      var _nodeRef$current;
      const element = document.elementFromPoint(event.clientX, event.clientY);
      // Check if the pointer was lifted on top of the button
      if (element && (_nodeRef$current = nodeRef.current) != null && _nodeRef$current.contains(element)) {
        // Wait until the next event loop (pointer events trigger before click events)
        onClickTimeoutId.current = setTimeout(() => {
          onClickTimeoutId.current = undefined;

          // Check if a click event was triggered
          if (onClickTriggered.current === false) {
            // If no click event was triggered, call `onAction`
            onAction == null ? void 0 : onAction();
          }
        }, 50);
      }
    }
  }, [onAction]);
  const iconOnly = icon && !content && !disclosure;
  const buttonClassNames = classNames(styles.Button, {
    [styles.iconOnly]: iconOnly
  });
  const actionMarkup = /*#__PURE__*/React__default.createElement(PlainAction, {
    slim: true,
    alignLeft: true,
    fullWidth: true,
    truncate: true,
    content: content,
    icon: icon,
    ariaExpanded: ariaExpanded,
    accessibilityLabel: getAccessibilityLabel(),
    pressed: pressed != null ? pressed : active,
    disclosure: disclosure,
    disabled: disabled,
    onAction: handleAction
  });
  const childrenMarkup = tooltip && !mobile ? /*#__PURE__*/React__default.createElement(Tooltip, {
    content: tooltip,
    dismissOnMouseOut: true
  }, actionMarkup) : actionMarkup;
  return /*#__PURE__*/React__default.createElement(PreventBlur, {
    element: "div",
    className: buttonClassNames,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    nodeRef: nodeRef
  }, childrenMarkup);
  function getAccessibilityLabel() {
    if (accessibilityLabel) {
      return accessibilityLabel;
    }
    return typeof tooltip === 'string' ? tooltip : undefined;
  }
}

export { Button };
